import React from "react";
import { useTranslation } from "react-i18next";

import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import Button from "@mui/material/Button";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";

import type { AssignedLimitation } from "src/shared/api-types/limitations";

interface CopyConfirmDialogProps {
  open: boolean;
  operationName: string;
  enabled: boolean;
  underSupervision: boolean;
  limitations: AssignedLimitation[];
  /** Other operations this will be copied to (already filtered to visible set) */
  otherOperationNames: string[];
  /** Whether an operation search filter is currently active */
  isFiltered: boolean;
  onConfirm: () => void;
  onClose: () => void;
}

const CopyConfirmDialog: React.FC<CopyConfirmDialogProps> = ({
  open,
  operationName,
  enabled,
  underSupervision,
  limitations,
  otherOperationNames,
  isFiltered,
  onConfirm,
  onClose,
}) => {
  const { t } = useTranslation();

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>{t("aircraft_management.copy_confirm_title")}</DialogTitle>
      <DialogContent>
        <Typography variant="body2" sx={{ mb: 2 }}>
          {t("aircraft_management.copy_confirm_message", { operation: operationName })}
        </Typography>
        <Typography variant="subtitle2" sx={{ mb: 1 }}>
          {t("aircraft_management.copy_confirm_settings")}
        </Typography>
        <Box sx={{ pl: 2, mb: 2 }}>
          <Typography variant="body2">
            • {t("aircraft_management.enabled")}: {t(enabled ? "yes" : "no")}
          </Typography>
          <Typography variant="body2">
            • {t("aircraft_management.under_supervision")}: {t(underSupervision ? "yes" : "no")}
          </Typography>
          <Typography variant="body2">
            • {t("limitations.title")}:{" "}
            {limitations.length > 0
              ? limitations.map((limitation) => limitation.displayValue).join(", ")
              : t("limitations.no_limitations")}
          </Typography>
        </Box>

        {isFiltered && (
          <Box sx={{ mt: 1 }}>
            <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
              {t("aircraft_management.copy_confirm_targets")}
            </Typography>
            <Box
              sx={{
                maxHeight: 150,
                overflow: "auto",
                border: "1px solid",
                borderColor: "divider",
                borderRadius: 1,
                p: 1,
                bgcolor: "#fff9c4",
              }}
            >
              {otherOperationNames.length === 0 ? (
                <Typography
                  variant="body2"
                  sx={{
                    color: "text.secondary",
                    fontStyle: "italic",
                  }}
                >
                  {t("aircraft_management.copy_confirm_no_targets")}
                </Typography>
              ) : (
                otherOperationNames.map((name) => (
                  <Typography key={name} variant="body2" sx={{ py: 0.25 }}>
                    • {name}
                  </Typography>
                ))
              )}
            </Box>
            <Typography
              variant="caption"
              sx={{
                color: "warning.main",
                display: "block",
                mt: 0.5,
              }}
            >
              {t("aircraft_management.copy_confirm_filtered_warning")}
            </Typography>
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{t("cancel")}</Button>
        <Button variant="contained" onClick={onConfirm} disabled={otherOperationNames.length === 0} autoFocus>
          {isFiltered ? t("aircraft_management.copy_apply_filtered") : t("aircraft_management.copy_apply_all")}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CopyConfirmDialog;
